import React from 'react';
import { GenerationProgression, getStageDisplayName } from '../../types/progression';
import Portal from '../Portal';

interface GenerationProcessingModalProps {
  open: boolean;
  progress?: number; // Legacy support - overall progress 0-100
  progression?: GenerationProgression; // New detailed progression object
}

export default function GenerationProcessingModal({
  open,
  progress,
  progression,
}: GenerationProcessingModalProps) {
  // Lock scroll when overlay is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  if (!open) return null;

  // Use progression if available, otherwise fall back to legacy progress
  // Add defensive checks to prevent errors from invalid progression state
  const safeProgression = progression && 
    typeof progression.overallProgress === 'number' && 
    progression.currentStage ? progression : null;
    
  const overallProgress = safeProgression?.overallProgress ?? progress ?? 0;
  const currentMessage = safeProgression?.message ?? 'Processing...';
  const currentStage = safeProgression?.currentStage ?? 'generating_video';

  // Get stage display name
  const stageName = getStageDisplayName(currentStage);

  // Define stage order for visual timeline
  // Only show user-facing stages: Enhance Prompt and Generate Video
  const stageOrder: Array<{ stage: string; label: string }> = [
    { stage: 'enhancing_prompt', label: 'Enhance Prompt' },
    { stage: 'generating_video', label: 'Generate Video' },
  ];

  // Find current stage index
  const currentStageIndex = stageOrder.findIndex(
    (s) => s.stage === currentStage
  );

  return (
    <Portal>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
        {/* Main Content Card */}
        <div className="bg-bg border border-white/20 rounded-xl p-8 shadow-lg">
          {/* Title */}
          <h2 className="text-3xl font-bold text-white mb-3 md:mb-4 text-center">
            Creating Your Masterpiece
          </h2>
          
          {/* Subtitle */}
          <p className="text-white/70 text-center mb-8">
            Our AI is bringing your vision to life. This usually takes 3–5 minutes.
          </p>

          {/* Current Stage Message */}
          <div className="mb-6 text-center">
            <p className="text-white/90 text-lg font-medium mb-1">
              {currentMessage}
            </p>
            <p className="text-white/60 text-sm">
              {stageName}
            </p>
          </div>

          {/* Progress Section - Enlarged */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white font-semibold text-lg">Progress</span>
              <span className="text-white font-semibold text-lg">{Math.round(overallProgress)}%</span>
            </div>
            
            {/* Progress Bar - Thicker */}
            <div className="w-full bg-white/10 rounded-full h-4 mb-2">
              <div
                className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(overallProgress, 100)}%` }}
              />
            </div>
          </div>

          {/* Warning Message about staying on page - Show always */}
          <div className="mt-6 mb-6 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-xs text-yellow-300 text-center">
              ⚠️ Please stay on this page while your video generates. Switching apps or tabs may interrupt the process.
            </p>
          </div>

          {/* Stage Timeline - Show if progression is available */}
          {progression && (
            <div className="mt-8">
              <div className="text-white/70 text-sm font-medium mb-4">
                Generation Stages
              </div>
              <div className="space-y-2">
                {stageOrder.map((stageInfo, index) => {
                  const stageData = progression.stages[stageInfo.stage as keyof typeof progression.stages];
                  const isActive = index === currentStageIndex;
                  const isCompleted = stageData?.completed ?? false;
                  const isUpcoming = index > currentStageIndex;

                  return (
                    <div
                      key={stageInfo.stage}
                      className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-blue-500/20 border border-blue-500/50'
                          : isCompleted
                          ? 'bg-green-500/10 border border-green-500/30'
                          : isUpcoming
                          ? 'bg-white/5 border border-white/10'
                          : 'bg-white/5 border border-white/10'
                      }`}
                    >
                      {/* Stage Icon */}
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isCompleted
                            ? 'bg-green-500'
                            : isActive
                            ? 'bg-blue-500'
                            : 'bg-white/20'
                        }`}
                      >
                        {isCompleted ? (
                          <svg
                            className="w-4 h-4 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : isActive ? (
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        ) : (
                          <div className="w-2 h-2 bg-white/50 rounded-full" />
                        )}
                      </div>

                      {/* Stage Label */}
                      <div className="flex-1">
                        <div
                          className={`text-sm font-medium ${
                            isActive
                              ? 'text-white'
                              : isCompleted
                              ? 'text-green-400'
                              : 'text-white/50'
                          }`}
                        >
                          {stageInfo.label}
                        </div>
                        {stageData?.message && isActive && (
                          <div className="text-xs text-white/60 mt-0.5">
                            {stageData.message}
                          </div>
                        )}
                      </div>

                      {/* Stage Progress */}
                      {isActive && progression.currentStageProgress !== undefined && (
                        <div className="text-xs text-white/60">
                          {Math.round(progression.currentStageProgress)}%
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </Portal>
  );
}

